import { z } from 'zod';
import { INITIATIVE_STATUSES } from './initiatives';
import { OBJECTIVE_STATUSES } from './objectives';
import { OBJECTIVE_PERFORMANCE_SOURCES } from './performance';
import { TRAFFIC_LIGHTS } from './status';

const light = z.enum(TRAFFIC_LIGHTS);
const performance = z.number().nullable();

/** Performance de um nível (objetivo, perspectiva, empresa) no mês e no acumulado do ano. */
const levelPoint = z.object({
  performance,
  light,
  ytdPerformance: performance,
  ytdLight: light,
});

/** Scorecard pré-calculado do mês (T-064b, RNF-04): empresa, perspectivas, objetivos e KPIs. */
export const scorecardSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  /** Quando os agregados foram calculados (null = ainda não há cálculo para este ano). */
  computedAt: z.string().nullable(),
  company: levelPoint.nullable(),
  perspectives: z.array(levelPoint.extend({ id: z.string().uuid(), name: z.string() })),
  objectives: z.array(
    levelPoint.extend({ id: z.string().uuid(), name: z.string(), perspectiveId: z.string().uuid() }),
  ),
  kpis: z.array(
    levelPoint.extend({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      unitLabel: z.string(),
      actual: z.number().nullable(),
      target: z.number().nullable(),
      ytdActual: z.number().nullable(),
      ytdTarget: z.number().nullable(),
    }),
  ),
});
export type Scorecard = z.infer<typeof scorecardSchema>;

export const scorecardQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
});

/** Série pré-calculada de um KPI no ano (mês a mês). */
export const kpiPerformanceSeriesSchema = z.object({
  kpiId: z.string().uuid(),
  year: z.number().int(),
  computedAt: z.string().nullable(),
  months: z.array(
    levelPoint.extend({
      month: z.number().int(),
      actual: z.number().nullable(),
      target: z.number().nullable(),
      ytdActual: z.number().nullable(),
      ytdTarget: z.number().nullable(),
    }),
  ),
});
export const kpiPerformanceSeriesQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
});

export const recalculateResultSchema = z.object({ years: z.array(z.number().int()) });

const refId = z.object({ id: z.string().uuid(), name: z.string() });

/** Mapa estratégico do mês (T-044a, RF-17): faixas, objetivos coloridos e setas de causa e efeito. */
export const strategyMapSchema = z.object({
  year: z.number().int(),
  month: z.number().int(),
  computedAt: z.string().nullable(),
  /** Perspectivas na ordem das faixas do PRD (§11.8): Aprendizado no alto até Financeira embaixo. */
  perspectives: z.array(refId),
  objectives: z.array(
    refId.extend({
      perspectiveId: z.string().uuid(),
      performance: z.number().nullable(),
      light: z.enum(TRAFFIC_LIGHTS),
      ownerName: z.string().nullable(),
      /** KPIs próprios ou consolidada dos que o desdobram (DEC-032); null sem cálculo. */
      source: z.enum(OBJECTIVE_PERFORMANCE_SOURCES).nullable(),
    }),
  ),
  /** Causa e efeito entre objetivos visíveis: de quem contribui para quem recebe. */
  relations: z.array(z.object({ fromId: z.string().uuid(), toId: z.string().uuid() })),
});
export type StrategyMap = z.infer<typeof strategyMapSchema>;

/** Painel lateral do objetivo no mapa (performance, KPIs, tendência, responsável, principais desvios). */
export const objectivePanelSchema = z.object({
  objective: refId.extend({
    perspective: refId,
    owner: refId.nullable(),
    department: refId.nullable(),
    status: z.enum(OBJECTIVE_STATUSES),
  }),
  month: levelPoint.nullable(),
  /** KPIs próprios ou consolidada dos que o desdobram (DEC-032); null sem cálculo. */
  source: z.enum(OBJECTIVE_PERFORMANCE_SOURCES).nullable(),
  /** Objetivos que o desdobram (visíveis), com a performance do mês — base do consolidado. */
  children: z.array(z.object({ id: z.string().uuid(), name: z.string(), performance, light })),
  /** Performance mensal do objetivo no ano (tendência). */
  trend: z.array(z.object({ month: z.number().int(), performance, light })),
  kpis: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      unitLabel: z.string(),
      weight: z.number().nullable(),
      actual: z.number().nullable(),
      target: z.number().nullable(),
      performance,
      light,
    }),
  ),
  /** KPIs mais abaixo da meta no mês (vermelhos e amarelos), do pior para o melhor. */
  deviations: z.array(
    z.object({
      kpiId: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      unitLabel: z.string(),
      actual: z.number().nullable(),
      target: z.number().nullable(),
      gap: z.number().nullable(),
      performance,
      light,
    }),
  ),
  /**
   * Iniciativas ativas que contribuem para o objetivo (T-044b): situação, líder, prazo e progresso. null quando o
   * perfil não acessa Iniciativas (o painel não as mostra nem diz que não há).
   */
  initiatives: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        status: z.enum(INITIATIVE_STATUSES),
        leader: refId.nullable(),
        endDate: z.string().nullable(),
        late: z.boolean(),
        progress: z.number(),
      }),
    )
    .nullable(),
});
export type ObjectivePanel = z.infer<typeof objectivePanelSchema>;
